import React, { useEffect, useState } from 'react';
import { getLabFusionRunReport, getLabFusionStatus, getLabTrustSnapshot, runLabFusion } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabMetric from '../components/LabMetric';
import SmartiLabBadge from '../components/SmartiLabBadge';
import SmartiLabRunOverlay from '../components/SmartiLabRunOverlay';
import { useSmartiLabContext } from '../SmartiLabContext';

export default function OpsConsolePage() {
  const { sharedQuery, isAdmin, refreshTrustSnapshot } = useSmartiLabContext();
  const [report, setReport] = useState(null);
  const [trust, setTrust] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [runOverlay, setRunOverlay] = useState({ open: false, stage: 'starting', errorMessage: '', errorTitle: '', errorDetails: '', recoverySteps: [], phaseKey: '', progressValue: null });

  useEffect(() => {
    Promise.all([getLabFusionRunReport(), getLabTrustSnapshot(sharedQuery)]).then(([runReport, trustSnapshot]) => {
      setReport(runReport);
      setTrust(trustSnapshot);
    }).catch((loadError) => setError(loadError.message || 'Unable to load the ops console.'));
  }, [sharedQuery]);

  const handleRun = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await runLabFusion();
      setRunOverlay({
        open: true,
        stage: 'running',
        errorMessage: '',
        errorTitle: '',
        errorDetails: '',
        recoverySteps: [],
        phaseKey: result?.runState?.phaseKey || '',
        progressValue: result?.runState?.progressPercent ?? 8,
      });
      let finished = false;
      while (!finished) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
        // eslint-disable-next-line no-await-in-loop
        const status = await getLabFusionStatus();
        if (status?.running) {
          setRunOverlay((current) => ({
            ...current,
            open: true,
            stage: 'running',
            phaseKey: status?.phaseKey || current.phaseKey,
            progressValue: status?.progressPercent ?? current.progressValue,
          }));
          continue;
        }

        finished = true;
        const [runReport, trustSnapshot] = await Promise.all([getLabFusionRunReport(), refreshTrustSnapshot?.() || getLabTrustSnapshot(sharedQuery)]);
        setReport(runReport);
        setTrust(trustSnapshot);
        if (status?.lastStatus === 'success') {
          setRunOverlay({
            open: true,
            stage: 'success',
            errorMessage: '',
            errorTitle: '',
            errorDetails: '',
            recoverySteps: [],
            phaseKey: 'completed',
            progressValue: 100,
          });
        } else {
          setRunOverlay({
            open: true,
            stage: 'error',
            errorMessage: status?.lastError || 'The fusion pipeline finished with an error.',
            errorTitle: 'Fusion pipeline failed',
            errorDetails: status?.lastError || '',
            recoverySteps: ['Inspect the latest warnings below.', 'Check the backend fusion log if needed.', 'Retry after the issue is fixed.'],
            phaseKey: status?.phaseKey || '',
            progressValue: status?.progressPercent ?? 0,
          });
        }
      }
    } catch (runError) {
      setError(runError.message || 'Could not start the fusion pipeline.');
      setRunOverlay({
        open: true,
        stage: 'error',
        errorMessage: runError.message || 'Could not start the fusion pipeline.',
        errorTitle: 'Fusion pipeline failed',
        errorDetails: runError.message || '',
        recoverySteps: ['Check the Smarti Lab backend state.', 'Verify the ML environment.', 'Retry when the issue is resolved.'],
        phaseKey: '',
        progressValue: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lab-page">
      <LabCard title="Fusion ops console" description="Admin view for run state, artifacts, warnings, and what changed since the last ML build." actions={isAdmin ? <button className="lab-button" type="button" disabled={busy} onClick={handleRun}>{busy ? 'Starting...' : 'Run now'}</button> : null}>
        {error ? <div className="lab-error-text">{error}</div> : null}
        <div className="lab-metric-grid">
          <LabMetric label="Last status" value={report?.status?.runStatus?.lastStatus || 'unknown'} />
          <LabMetric label="Last success" value={report?.status?.runStatus?.lastEndedAt || '—'} />
          <LabMetric label="Artifact warnings" value={report?.qualityWarnings?.length ?? 0} />
          <LabMetric label="Confidence" value={trust?.confidenceLevel || 'unknown'} />
        </div>
        <div className="lab-chip-row">
          {(report?.qualityWarnings || []).slice(0, 8).map((warning, index) => (
            <SmartiLabBadge key={`${warning.warning_type}-${index}`} variant={String(warning.severity || '').toLowerCase() === 'high' ? 'danger' : 'warning'} label={warning.warning_type || warning.message} />
          ))}
        </div>
      </LabCard>
      <SmartiLabRunOverlay
        open={runOverlay.open}
        stage={runOverlay.stage}
        errorMessage={runOverlay.errorMessage}
        errorTitle={runOverlay.errorTitle}
        errorDetails={runOverlay.errorDetails}
        recoverySteps={runOverlay.recoverySteps}
        phaseKey={runOverlay.phaseKey}
        progressValue={runOverlay.progressValue}
        onClose={() => setRunOverlay((current) => ({ ...current, open: false }))}
      />
    </div>
  );
}
